import { useParams, useOutletContext, Link, Navigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Star, ArrowUpRight } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { buildPath, canonicalSlug } from "@/lib/routes";
import { toLangSlug } from "@/lib/slug_map";
import { useLang } from "@/hooks/useLang";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import { Stagger, StaggerItem } from "@/components/motion/Reveal";
import LeadMagnet from "@/components/LeadMagnet";
import { breadcrumbSchema } from "@/lib/schema";

function Rating({ value }) {
  return (
    <div className="flex items-center gap-1">
      {[...Array(5)].map((_, i) => (
        <Star key={i} size={13} weight="fill" className={i < Math.round(value) ? "text-primary" : "text-border"} />
      ))}
      <span className="ml-1 font-mono text-xs text-muted-foreground">{value.toFixed(1)}</span>
    </div>
  );
}

export default function TechStackDetail() {
  const { slug: urlSlug } = useParams();
  const { lang, t } = useLang();
  const { profile } = useOutletContext();
  const daSlug = canonicalSlug("stack", urlSlug);
  const { data: stack, isLoading } = useQuery({ queryKey: ["stack", daSlug], queryFn: () => api.getTechStack(daSlug) });

  const canonicalUrlSlug = toLangSlug("stack", daSlug, lang);
  if (canonicalUrlSlug && urlSlug !== canonicalUrlSlug) {
    return <Navigate to={buildPath("techstack", lang, { slug: daSlug })} replace />;
  }

  if (isLoading || !stack) return <div className="px-6 md:px-20 py-32 text-muted-foreground">…</div>;
  const c = stack[lang] || stack.da;
  const path = buildPath("techstack", lang, { slug: daSlug });

  return (
    <>
      <Seo routeKey="techstack" params={{ slug: daSlug }} lang={lang} title={c.meta_title} description={c.meta_description}
        jsonLd={[breadcrumbSchema([{ name: t("nav_home"), path: buildPath("home", lang) }, { name: t("nav_techstacks"), path: buildPath("techstacks", lang) }, { name: c.title, path }])]} />
      <PageHero title={c.title} description={c.tagline}
        crumbs={[{ label: t("nav_home"), to: "home" }, { label: t("nav_techstacks"), to: "techstacks" }, { label: c.title }]} />

      <section className="px-6 md:px-12 lg:px-20 py-12">
        <p className="max-w-3xl text-lg text-muted-foreground font-light leading-relaxed">{c.intro}</p>
        <div className="mt-8 grid gap-5 md:grid-cols-2 max-w-4xl">
          <div className="rounded-xl border border-border bg-card p-6">
            <h2 className="font-heading text-lg font-bold">{lang === "da" ? "Sådan vælger jeg værktøjer" : "How I choose tools"}</h2>
            <p className="mt-3 text-sm text-muted-foreground font-light leading-relaxed">
              {lang === "da"
                ? "Et værktøj skal løse et reelt problem hurtigere eller bedre, end jeg kan uden det. Jeg vægter pålidelighed, datakvalitet og hvor godt det spiller sammen med resten af min stack — ikke antallet af funktioner."
                : "A tool has to solve a real problem faster or better than I can without it. I weigh reliability, data quality and how well it plays with the rest of my stack — not the number of features."}
            </p>
          </div>
          <div className="rounded-xl border border-border bg-card p-6">
            <h2 className="font-heading text-lg font-bold">{lang === "da" ? "Sådan bruger jeg dem" : "How I use them"}</h2>
            <p className="mt-3 text-sm text-muted-foreground font-light leading-relaxed">
              {lang === "da"
                ? "Værktøjerne herunder bruger jeg i praksis på kunde- og egne projekter. Jeg tester alt selv, før jeg anbefaler det — og skifter uden tøven, hvis noget bedre dukker op."
                : "I use the tools below in practice on client and own projects. I test everything myself before recommending it — and switch without hesitation if something better comes along."}
            </p>
          </div>
        </div>
      </section>

      <section className="px-6 md:px-12 lg:px-20 pb-16">
        <Stagger className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {(stack.tools || []).map((tool) => {
            const tc = tool[lang] || tool.da;
            return (
              <StaggerItem key={tool.name}>
                <Link to={buildPath("tool", lang, { stack: daSlug, tool: tool.slug })} data-testid={`tool-${tool.slug}`}
                  className="group relative flex h-full flex-col overflow-hidden rounded-xl border border-border bg-card p-6 transition-all duration-500 hover:border-primary hover:shadow-[0_0_40px_-14px_hsl(var(--primary)/0.5)]">
                  <div className="pointer-events-none absolute inset-x-0 -top-px h-px bg-gradient-to-r from-transparent via-primary to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
                  <div className="flex items-start justify-between">
                    <div className="flex h-11 w-11 items-center justify-center rounded-lg border border-border bg-secondary font-heading text-lg font-black text-primary">
                      {tool.name.charAt(0)}
                    </div>
                    <span className="rounded-full border border-border px-2.5 py-1 font-mono text-[10px] uppercase tracking-wider text-muted-foreground">{tool.category}</span>
                  </div>
                  <h3 className="mt-5 font-heading text-xl font-bold tracking-tight">{tool.name}</h3>
                  <p className="mt-2 flex-1 text-sm text-muted-foreground font-light leading-relaxed">{tc.description}</p>
                  <div className="mt-5 flex items-center justify-between">
                    <Rating value={tool.rating} />
                    <span className="font-mono text-xs text-muted-foreground">{tc.pricing}</span>
                  </div>
                  <div className="mt-4 flex items-center gap-1.5 border-t border-border pt-4 text-sm font-medium text-primary">
                    {t("read_more")}
                    <ArrowUpRight size={15} weight="bold" className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </div>
                </Link>
              </StaggerItem>
            );
          })}
        </Stagger>
        <p className="mt-8 max-w-2xl font-mono text-xs text-muted-foreground/60">
          {lang === "da"
            ? "* Nogle links er affiliate-links. Jeg kan tjene en lille kommission uden ekstra omkostning for dig. Jeg anbefaler kun værktøjer, jeg selv bruger og står inde for."
            : "* Some links are affiliate links. I may earn a small commission at no extra cost to you. I only recommend tools I use and stand behind myself."}
        </p>
      </section>

      {/* Comparison table */}
      <section className="border-t border-border px-6 md:px-12 lg:px-20 py-16">
        <h2 className="mb-6 font-heading text-2xl md:text-3xl font-bold tracking-tight">
          {lang === "da" ? "Sammenlign værktøjerne" : "Compare the tools"}
        </h2>
        <div className="overflow-x-auto rounded-xl border border-border">
          <table className="w-full text-left text-sm" data-testid="stack-compare-table">
            <thead>
              <tr className="border-b border-border bg-secondary/50">
                <th className="px-5 py-3 font-heading font-bold">{lang === "da" ? "Værktøj" : "Tool"}</th>
                <th className="px-5 py-3 font-heading font-bold">{lang === "da" ? "Kategori" : "Category"}</th>
                <th className="px-5 py-3 font-heading font-bold">{lang === "da" ? "Vurdering" : "Rating"}</th>
                <th className="px-5 py-3 font-heading font-bold">{lang === "da" ? "Pris" : "Price"}</th>
                <th className="px-5 py-3" />
              </tr>
            </thead>
            <tbody>
              {(stack.tools || []).map((tool) => {
                const tc = tool[lang] || tool.da;
                return (
                  <tr key={tool.name} className="border-b border-border last:border-0">
                    <td className="px-5 py-3 font-medium">{tool.name}</td>
                    <td className="px-5 py-3 text-muted-foreground">{tool.category}</td>
                    <td className="px-5 py-3 text-muted-foreground">{tool.rating.toFixed(1)} / 5</td>
                    <td className="px-5 py-3 text-muted-foreground">{tc.pricing}</td>
                    <td className="px-5 py-3 text-right">
                      <a href={tool.url} target="_blank" rel="noopener noreferrer sponsored"
                        className="inline-flex items-center gap-1 text-primary hover:underline">
                        {t("visit")} <ArrowUpRight size={13} weight="bold" />
                      </a>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>
      <LeadMagnet profile={profile} />
    </>
  );
}
